/*
 * Module Registry
 * ----------------
 * Single source of truth for discoverable application modules.
 * Module visibility is derived from route access rules defined in route-access.ts.
 * This ensures menu visibility matches route protection.
 *
 * Add a module:
 *  1. Append a new object to MODULES with a unique id & route.
 *  2. Provide category, title, short description.
 *  3. Define access requirements in route-access.ts for the route.
 *  4. If module is experimental, set flags: { beta: true }.
 */

import {
  getRouteAccessRequirement,
  checkAccess,
} from '../../utility/route-access';
import type { UserAccessManager } from '../../utility/user-access-manager';
import { terms } from '../../language/terms';

// Categories (expandable); kept as string union for ergonomic matching & CSS hooks.
export type ModuleCategory =
  'core' | 'tools' | 'documents' | 'maintenance' | 'audit' | 'admin' | 'other';

/**
 * Submenu item for module quick links.
 * Shown as smaller links below the main module card content.
 */
export interface ModuleSubmenuItem {
  /** Display label for the submenu item */
  label: string;
  /** Full route path WITHOUT leading slash, the same shape as ModuleMeta.route - never module-relative */
  route: string;
  /** Optional icon name (Shoelace/Bootstrap icon) */
  iconName?: string;
  /** If true, only show this item to users who can create content (processors/creators) */
  requiresCreator?: boolean;
}

export interface ModuleMeta {
  id: string; // stable identifier
  route: string; // base route path WITHOUT leading slash (e.g. 'module/gdc')
  title: string; // short display name
  description: string; // concise summary
  category: ModuleCategory; // grouping key for cross-cutting tools; also used for intra-group ordering
  /**
   * Optional module group. When set, the module is rendered inside a module-
   * group section on the modules hub (e.g. "Product Registry") instead of in a
   * category section. Modules without a group fall back to category rendering.
   */
  moduleGroup?: string;
  order?: number; // optional ordering (lower = earlier)
  beta?: boolean; // mark as beta/preview
  archived?: boolean; // hide unless explicitly requested
  iconName?: string; // optional Shoelace/Bootstrap icon name
  heroGraphic?: string; // optional path/URL to hero graphic (64x64 or scalable)
  keywords?: string[]; // search terms
  submenu?: ModuleSubmenuItem[]; // optional quick links shown on the card
}

export interface ResolvedModule extends ModuleMeta {
  visible: boolean; // computed visibility after filtering
}

/**
 * Module group definition. A group represents a logical product area
 * (e.g. Product Registry, Glass Accreditation) that owns multiple surfaces.
 * The modules hub renders one section per group, above the category sections.
 */
export interface ModuleGroupMeta {
  id: string; // matches ModuleMeta.moduleGroup
  title: string; // section header
  description?: string; // optional subtitle shown under the header
  order: number; // section order on the hub (lower = earlier)
  iconName?: string; // optional icon shown next to the header
}

export interface ResolvedModuleGroup extends ModuleGroupMeta {
  modules: ResolvedModule[];
}

// Module groups. Keep sorted by order for readability.
export const MODULE_GROUPS: ModuleGroupMeta[] = [
  {
    id: 'product-registry',
    title: terms.productRegistry.value,
    description: `${terms.loginGroup.value}-owned fenestration ${terms.product.lowerPlural}, test reports, and support data`,
    order: 10,
    iconName: 'boxes',
  },
  {
    id: 'glass-accreditation',
    title: terms.glassAccreditation.value,
    description: `AS/NZS 2208 and AS 4666 certificate management for ${terms.primeGlassManufacturer.lowerPlural}`,
    order: 20,
    iconName: 'award',
  },
];

// Core registry list. KEEP SORTED by category then order then title for maintainability.
// Access control is derived from route-access.ts based on the module's route.
export const MODULES: ModuleMeta[] = [
  {
    id: 'home',
    route: '',
    title: 'Home',
    description: 'Landing dashboard & quick links',
    category: 'core',
    order: 0,
    iconName: 'house',
  },
  {
    id: 'gdc',
    route: 'module/gdc',
    title: `Cloud ${terms.storedFile.plural}`,
    description: `Important Documents & ${terms.storedFile.value} Management`,
    heroGraphic: '/assets/icons/modules/gdc-hero.png',
    category: 'documents',
    order: 10,
    /**
     * DEPRECATED (issue #302). Withdrawn from the modules hub rather than repaired.
     *
     * GDC was the first document-centric module and was meant to be the reference for
     * later ones. Glass Accreditation has since taken that role as a complete, in-production
     * example, so GDC no longer earns the cost of maintaining a parallel implementation.
     *
     * It is also already non-functional: `agwa-file-browser` moved to a store-driven API in
     * `5e541dc8` (2025-12-28) and `gdc-document-view` was never migrated, so its document
     * page throws on mount and its legacy `<gdc-files>` fallback was deleted. Archiving is
     * the honest state - the surface must not advertise a capability that does not work.
     *
     * `archived` hides it from the hub while leaving the route reachable, so existing links
     * and stored data stay accessible for whoever retires or migrates it. See
     * `src/modules/gdc/README.md`.
     */
    archived: true,
  },
  {
    id: 'workflow-sandbox',
    route: 'sandbox/workflows',
    title: 'Workflow Sandbox',
    description: 'Create, test, and version workflow definitions',
    category: 'tools',
    order: 25,
    iconName: 'diagram-3',
    beta: true,
    keywords: ['workflow', 'sandbox', 'yaml', 'automation', 'testing'],
    submenu: [
      {
        label: 'New Workflow',
        route: 'sandbox/workflows/new',
        iconName: 'plus-circle',
        requiresCreator: true,
      },
    ],
  },
  {
    id: 'prod-test-thumb-sandbox',
    route: 'experimental/prod-test-thumb-generator',
    title: `${terms.product.value} Test Thumbnail Sandbox`,
    description: `Visual frame composition editor for testing ${terms.product.lower} test thumbnails`,
    category: 'tools',
    order: 26,
    iconName: 'palette',
    beta: true,
    keywords: [
      'thumbnail',
      'product',
      'test',
      'svg',
      'frame',
      'composition',
      'experimental',
    ],
  },
  {
    id: 'members',
    route: 'maintenance/companies',
    title: terms.loginGroup.plural,
    description: `${terms.loginGroup.value} directory & management`,
    category: 'maintenance',
    order: 30,
    iconName: 'people',
  },
  {
    id: 'staff',
    route: 'module/staff',
    title: `${terms.agwa.value} ${terms.staffUser.value}`,
    description: `${terms.agwa.value} ${terms.staffUser.lower} ${terms.loginUser.lower} management`,
    category: 'maintenance',
    order: 35,
    iconName: 'person-badge',
  },
  {
    id: 'audit-log',
    route: 'module/audit-log',
    title: 'Security Audit',
    description: 'Security changes and access audit log',
    category: 'audit',
    order: 10,
    iconName: 'shield-check',
  },
  {
    id: 'user-activity',
    route: 'module/user-activity',
    title: `${terms.loginUser.value} Activity`,
    description: 'Live sessions, session history, and usage analytics',
    category: 'audit',
    order: 20,
    iconName: 'activity',
    keywords: [
      'activity',
      'sessions',
      'analytics',
      'usage',
      'tracking',
      'live',
    ],
  },
  {
    id: 'email-audit',
    route: 'module/email-audit',
    title: 'Email History',
    description: 'System email notifications and delivery history',
    category: 'audit',
    order: 30,
    iconName: 'envelope',
    keywords: ['email', 'notification', 'sent', 'history', 'log'],
  },
  {
    id: 'glass-accreditation-certificates',
    route: 'module/glass-accreditation/certificates',
    title: terms.glassAccreditation.value,
    description: `AS/NZS 2208 and AS 4666 certificate management for ${terms.primeGlassManufacturer.lowerPlural}`,
    category: 'documents',
    moduleGroup: 'glass-accreditation',
    order: 15,
    iconName: 'award',
    keywords: [
      'accreditation',
      'as2208',
      'as4666',
      'glass',
      'certificate',
      'processor',
    ],
    submenu: [
      {
        label: 'Drafts',
        route: 'module/glass-accreditation/certificates/my-drafts',
        iconName: 'file-earmark-text',
        requiresCreator: true,
      },
      {
        label: 'Needs Audit',
        route: 'module/glass-accreditation/certificates/needs-audit',
        iconName: 'clipboard-check',
      },
      {
        label: 'Expiring Soon',
        route: 'module/glass-accreditation/certificates/expiring-soon',
        iconName: 'hourglass-split',
      },
      {
        label: 'Review Due',
        route: 'module/glass-accreditation/certificates/review-due',
        iconName: 'calendar-check',
      },
    ],
  },
  {
    id: 'glass-accreditation-settings',
    route: 'module/glass-accreditation/settings',
    title: `${terms.glassAccreditation.value} Settings`,
    description:
      'Manage accreditation reference data (glass types, interlayers, brands, etc.)',
    category: 'admin',
    moduleGroup: 'glass-accreditation',
    order: 50,
    iconName: 'gear-wide-connected',
    keywords: [
      'accreditation',
      'as2208',
      'as4666',
      'glass',
      'settings',
      'lookup',
      'reference',
    ],
  },
  {
    id: 'product-registry-products',
    route: 'module/product-registry/products',
    title: terms.productRegistry.value,
    description: `Manage ${terms.loginGroup.lower}-owned fenestration ${terms.product.lowerPlural}, brandings, and support data links`,
    category: 'documents',
    moduleGroup: 'product-registry',
    order: 16,
    iconName: 'boxes',
    keywords: [
      'product',
      'registry',
      'fenestration',
      'brandings',
      'series',
      'frame',
      'operating type',
    ],
    submenu: [
      {
        label: `New ${terms.product.value}`,
        route: 'module/product-registry/products/new',
        iconName: 'plus-circle',
        requiresCreator: true,
      },
    ],
  },
  {
    id: 'product-registry-reports',
    route: 'module/product-registry/reports',
    // One quick link per report type (architect, 2026-09-06: "sub menus for the 3 new
    // report buttons"). AS 2047 and AS 5218 have create routes of their own; a bushfire
    // report is product-scoped, so its link opens the reports list with the product
    // picker already showing, exactly what the list's New Report menu does.
    submenu: [
      {
        label: 'New AS 2047 report',
        route: 'module/product-registry/reports/as2047/new',
        iconName: 'plus-circle',
        requiresCreator: true,
      },
      {
        label: 'New AS 5218 report',
        route: 'module/product-registry/reports/as5218/new',
        iconName: 'plus-circle',
        requiresCreator: true,
      },
      {
        label: 'New AS 1530 bushfire report',
        route: 'module/product-registry/reports?new=bushfire',
        iconName: 'plus-circle',
        requiresCreator: true,
      },
    ],
    // The one term the route title and the breadcrumb root also read, so a rename lands in all
    // three at once (architect, 2026-09-06).
    title: terms.productTestReport.plural,
    description: `Review and manage ${terms.product.lower} test reports (AS 2047, AS 5218, AS 1530 bushfire)`,
    category: 'documents',
    moduleGroup: 'product-registry',
    order: 17,
    iconName: 'clipboard-data',
    keywords: [
      'test',
      'report',
      'as2047',
      'as1530',
      'as5218',
      'audit',
      'compliance',
    ],
  },
  {
    id: 'product-registry-settings',
    route: 'module/product-registry/settings',
    title: `${terms.productRegistry.value} Settings`,
    description: `Manage ${terms.product.lower} operating types and frame material lookup data`,
    category: 'admin',
    moduleGroup: 'product-registry',
    order: 51,
    iconName: 'sliders',
    keywords: [
      'product',
      'registry',
      'settings',
      'operating type',
      'frame material',
      'lookup',
      'reference',
    ],
  },
  {
    id: 'settings',
    route: 'settings',
    title: 'Settings',
    description: 'Application preferences',
    category: 'core',
    order: 40,
    iconName: 'gear',
    archived: true,
  },
  {
    // The State Colours registry page (D-040 / state-colour-standard.md): every workflow
    // state in the suite with the badge colour it wears, previewed in light and dark at
    // once. Suite-wide rather than module-scoped, so it lives under /settings.
    id: 'badge-colours',
    route: 'settings/badge-colours',
    title: 'Badge Colours',
    description:
      'The colour every badge wears - workflow states and classification chips - suite-wide, previewed in light and dark',
    category: 'admin',
    order: 52,
    iconName: 'palette',
    keywords: ['state', 'status', 'colour', 'color', 'badge', 'palette'],
  },
  {
    id: 'about',
    route: 'about',
    title: 'About',
    description: 'Application information & version',
    category: 'core',
    order: 100,
    iconName: 'info-circle',
  },
];

// Access shape for module visibility - uses the shared route-access system
export interface AccessLike {
  access?: UserAccessManager;
  isLoggedIn: boolean;
}

/** Compute visibility for a module given access */
function isModuleVisible(m: ModuleMeta, accessInfo?: AccessLike): boolean {
  if (m.archived) return false;

  // Get access requirements from route-access.ts based on the module's route
  const routePath = m.route.startsWith('/') ? m.route : `/${m.route}`;
  const requirement = getRouteAccessRequirement(routePath);

  if (!requirement) {
    // No specific requirement - visible to all
    return true;
  }

  const result = checkAccess(
    accessInfo?.access,
    accessInfo?.isLoggedIn ?? false,
    requirement
  );

  return result.allowed;
}

/** Return modules after filtering & sorting */
export function listModules(accessInfo?: AccessLike): ResolvedModule[] {
  return MODULES.map((m) => ({ ...m, visible: isModuleVisible(m, accessInfo) }))
    .filter((m) => m.visible)
    .sort(
      (a, b) =>
        (a.order ?? 999) - (b.order ?? 999) || a.title.localeCompare(b.title)
    );
}

/** Group modules by category */
export function listModulesGrouped(
  access?: AccessLike
): Record<ModuleCategory, ResolvedModule[]> {
  const groups: Record<ModuleCategory, ResolvedModule[]> = {
    core: [],
    tools: [],
    documents: [],
    maintenance: [],
    audit: [],
    admin: [],
    other: [],
  };
  for (const m of listModules(access)) {
    groups[m.category].push(m);
  }
  return groups;
}

/**
 * Layout used by the modules hub: module-group sections first (one per
 * MODULE_GROUPS entry that has at least one visible module), followed by
 * category buckets for any remaining modules that do not belong to a group.
 */
export interface ModulesHubLayout {
  groups: ResolvedModuleGroup[];
  categories: Record<ModuleCategory, ResolvedModule[]>;
}

export function listModulesByGroup(access?: AccessLike): ModulesHubLayout {
  const visible = listModules(access);

  const groupMap = new Map<string, ResolvedModule[]>();
  const ungrouped: ResolvedModule[] = [];
  for (const m of visible) {
    if (m.moduleGroup) {
      const bucket = groupMap.get(m.moduleGroup);
      if (bucket) {
        bucket.push(m);
      } else {
        groupMap.set(m.moduleGroup, [m]);
      }
    } else {
      ungrouped.push(m);
    }
  }

  const groups: ResolvedModuleGroup[] = MODULE_GROUPS.filter((g) =>
    groupMap.has(g.id)
  )
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((g) => ({ ...g, modules: groupMap.get(g.id) || [] }));

  const categories: Record<ModuleCategory, ResolvedModule[]> = {
    core: [],
    tools: [],
    documents: [],
    maintenance: [],
    audit: [],
    admin: [],
    other: [],
  };
  for (const m of ungrouped) {
    categories[m.category].push(m);
  }

  return { groups, categories };
}

/**
 * Resolve a list of module ids to the modules this caller may actually see, in the order given.
 *
 * <p>The order is the CALLER'S, not the registry's, because the ids come from a history: recents are
 * most-recent-first and that ordering is the whole point of them.</p>
 *
 * WHY THIS IS SHARED RATHER THAN DONE PER SURFACE. Recents persist as bare module ids for 30 days,
 * so an entry outlives the module being archived or the user losing access to it. Resolving through
 * the visibility filter is what stops a withdrawn module staying reachable from a quick link after
 * it has been hidden everywhere else - the defect issue #302 describes for the deprecated GDC
 * surface. The home page has done this correctly since then; putting a second copy of the rule in
 * the new menu would be one edit away from the two disagreeing, and the one that forgot would be
 * the one still offering the withdrawn module.
 */
/**
 * A section of the WORKFLOW menu: a kind of work, not a module.
 *
 * The modules hub groups by module and then by category, so everything you CONFIGURE sits beneath
 * the module that owns it and "change a lookup" means first knowing which module owns the lookup.
 * AGWA staff do not think that way - they think in the work: deal with a company, register a
 * product, issue a certificate, read a report, change a setting. These sections are that ordering,
 * and the inversion that matters is Settings: every module's settings gather in one destination.
 *
 * MODULE IDS, NOT MODULES. The section is a view over the registry rather than a second copy of it,
 * so a module's title, icon, description, route and submenu keep exactly one source. An id naming
 * nothing is caught by the guard test rather than silently rendering an empty column.
 */
export interface WorkflowSection {
  id: string;
  title: string;
  moduleIds: readonly string[];
}

/**
 * The workflow menu's sections, in reading order: four columns, filled row by row.
 *
 * `home` is deliberately absent. A menu listing "Home" offers the reader the page they are standing
 * on, and it is already one click away in the header.
 */
export const WORKFLOW_SECTIONS: readonly WorkflowSection[] = [
  {
    id: 'companies',
    title: terms.loginGroup.plural,
    moduleIds: ['members', 'staff'],
  },
  {
    // Test reports sit here rather than under a section of their own: a report is read as part of
    // dealing with a product, not as a separate errand, and a "Reports" heading holding one entry
    // asked the reader to know which module owned it before they could find it.
    id: 'products',
    title: terms.product.plural,
    moduleIds: ['product-registry-products', 'product-registry-reports'],
  },
  {
    id: 'glass',
    title: terms.glassAccreditation.value,
    moduleIds: ['glass-accreditation-certificates'],
  },
  {
    id: 'settings',
    title: 'Settings',
    moduleIds: [
      'product-registry-settings',
      'glass-accreditation-settings',
      'badge-colours',
      'settings',
    ],
  },
  {
    id: 'audit',
    title: 'Audit',
    moduleIds: ['audit-log', 'user-activity', 'email-audit'],
  },
  {
    id: 'tools',
    title: 'Tools',
    // GDC is retired (D-175) and no longer listed here; archived, it was already left out of the
    // menu by isModuleVisible.
    moduleIds: ['workflow-sandbox', 'prod-test-thumb-sandbox'],
  },
  {
    id: 'system',
    title: 'System',
    moduleIds: ['about'],
  },
];

export function resolveVisibleModuleIds(
  ids: readonly string[],
  access?: AccessLike
): ResolvedModule[] {
  const visible = new Map(listModules(access).map((m) => [m.id, m]));

  const resolved: ResolvedModule[] = [];
  for (const id of ids) {
    const module = visible.get(id);
    if (module) resolved.push(module);
  }
  return resolved;
}

export function getModule(id: string): ModuleMeta | undefined {
  return MODULES.find((m) => m.id === id);
}

export function findModuleByRoute(route: string): ModuleMeta | undefined {
  return MODULES.find((m) => m.route === route);
}

// DEV safeguard: uniqueness checks (only runs when not production build)
// Dev-time duplication validation (skips in production builds w/out relying on Node types)
(function devValidate() {
  function detectProd(): boolean {
    // Prefer import.meta.env if available
    const g = globalThis as unknown as {
      importMeta?: { env?: Record<string, unknown> };
      process?: { env?: Record<string, string> };
    };
    const prodViaImportMeta =
      g.importMeta?.env?.PROD === true ||
      g.importMeta?.env?.NODE_ENV === 'production';
    const prodViaProcess = g.process?.env?.NODE_ENV === 'production';
    return !!(prodViaImportMeta || prodViaProcess);
  }
  if (detectProd()) return;
  const ids = new Set<string>();
  const routes = new Set<string>();
  for (const m of MODULES) {
    if (ids.has(m.id)) {
      console.warn('[module-registry] Duplicate id', m.id);
    }
    ids.add(m.id);
    if (routes.has(m.route)) {
      console.warn('[module-registry] Duplicate route', m.route);
    }
    routes.add(m.route);
  }
})();
